import { useState, useEffect } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, ImageOverlay, Polygon, useMap, Tooltip } from "react-leaflet";
import { Maximize, X } from "lucide-react";
import { useTranslation } from "../../../core/hooks/useTranslation";
import { resolveImageUrl } from "../../../core/utils/image";
import { RemoteImage } from "../../../core/components/RemoteImage";

// Helper to load image dimensions
const getImageDimensions = (src: string): Promise<{ w: number; h: number }> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.width, h: img.height });
        img.onerror = reject;
        img.src = src;
    });
};

function MapController({ bounds }: { bounds: L.LatLngBoundsExpression }) {
    const map = useMap();
    useEffect(() => {
        if (bounds) map.fitBounds(bounds);
    }, [bounds, map]);
    return null;
}

interface FloorPlanViewerProps {
    floor: any;
    units: any[];
    onClose: () => void;
}

export const FloorPlanViewer = ({ floor, units, onClose }: FloorPlanViewerProps) => {
    const { t } = useTranslation();
    const [imageBounds, setImageBounds] = useState<L.LatLngBoundsExpression | null>(null);
    const [selectedUnit, setSelectedUnit] = useState<any | null>(null);

    // Filter units for this floor that have valid shapes (must have lat/lng points)
    const mappedUnits = units.filter(u => {
        if (!Array.isArray(u.blueprintShape) || u.blueprintShape.length < 3) return false;
        // Verify points actually contain numeric pairs [lat, lng] to prevent leaflet crashes
        return u.blueprintShape.every((p: any) => Array.isArray(p) && typeof p[0] === 'number' && typeof p[1] === 'number');
    });

    useEffect(() => {
        if (floor?.image) {
            const resolvedSrc = resolveImageUrl(floor.image);
            getImageDimensions(resolvedSrc).then(({ w, h }) => {
                setImageBounds([[0, 0], [h, w]]);
            });
        }
    }, [floor]);

    if (!floor || !imageBounds) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-white rounded-2xl overflow-hidden w-full max-w-6xl h-[85vh] flex flex-col shadow-2xl relative">

                {/* Header */}
                <div className="p-4 border-b flex justify-between items-center bg-white z-10">
                    <div>
                        <h3 className="font-display font-bold text-xl text-secondary">{floor.name}</h3>
                        <p className="text-sm text-slate-500">{mappedUnits.length} {t('project.unitsMapped', 'Units Mapped')}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                    >
                        <X size={24} className="text-slate-500" />
                    </button>
                </div>

                <div className="flex flex-1 overflow-hidden">
                    {/* Map Area */}
                    <div className="flex-1 relative bg-slate-100">
                        <MapContainer
                            crs={L.CRS.Simple}
                            bounds={imageBounds}
                            className="h-full w-full z-0"
                            minZoom={-1}
                            maxZoom={1}
                            scrollWheelZoom={true}
                            attributionControl={false}
                        >
                            <ImageOverlay url={resolveImageUrl(floor.image)} bounds={imageBounds} />

                            {mappedUnits.map((unit) => {
                                const isSold = unit.status === 'SOLD';
                                const isResale = unit.status === 'RESALE';

                                return (
                                    <Polygon
                                        key={unit.id}
                                        positions={unit.blueprintShape} // Already stored as [lat, lng] array
                                        pathOptions={{
                                            color: selectedUnit?.id === unit.id ? "#2563eb" : isSold ? "#ef4444" : isResale ? "#f97316" : "#10b981",
                                            fillColor: selectedUnit?.id === unit.id ? "#2563eb" : isSold ? "#ef4444" : isResale ? "#f97316" : "#10b981",
                                            fillOpacity: selectedUnit?.id === unit.id ? 0.5 : isSold ? 0.6 : isResale ? 0.5 : 0.3,
                                            weight: selectedUnit?.id === unit.id ? 2 : 2
                                        }}
                                        eventHandlers={{
                                            click: () => setSelectedUnit(unit),
                                            mouseover: (e) => {
                                                e.target.setStyle({ fillOpacity: 0.7 });
                                            },
                                            mouseout: (e) => {
                                                if (selectedUnit?.id !== unit.id) {
                                                    e.target.setStyle({ fillOpacity: isSold ? 0.6 : 0.3 });
                                                }
                                            }
                                        }}
                                    >
                                        <Tooltip
                                            sticky={!isSold && !isResale}
                                            permanent={isSold || isResale}
                                            direction="center"
                                            className={isSold || isResale ? "bg-transparent border-0 shadow-none font-bold text-white text-shadow-md text-center" : ""}
                                        >
                                            <div className="text-center">
                                                {(isSold || isResale) ? (
                                                    <span className="text-xs uppercase tracking-wider drop-shadow-md">
                                                        {isSold ? t('status.sold', 'SOLD') : t('status.resale', 'RESALE')}
                                                    </span>
                                                ) : (
                                                    <>
                                                        <div className="font-bold">{unit.slug}</div>
                                                        <div className="text-xs">{unit.type} • {unit.area}m²</div>
                                                    </>
                                                )}
                                            </div>
                                        </Tooltip>
                                    </Polygon>
                                );
                            })}

                            <MapController bounds={imageBounds} />
                        </MapContainer>

                        {/* Instructions Overlay */}
                        <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur px-3 py-2 rounded-lg shadow text-xs text-slate-600 z-[400] pointer-events-none">
                            <p>{t('project.mapInstructions', 'Click on a unit to view details')}</p>
                        </div>
                    </div>

                    {/* Sidebar Details (for selected unit) */}
                    <div className={`hidden md:block bg-white border-l overflow-y-auto transition-all duration-300 ${selectedUnit ? 'w-80 translate-x-0 opacity-100' : 'w-0 translate-x-full opacity-0'}`}>
                        {selectedUnit && (
                            <div className="p-6 space-y-6 h-full flex flex-col">
                                <div>
                                    <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden mb-4">
                                        <RemoteImage src={selectedUnit.image} className="w-full h-full object-cover" />
                                    </div>
                                    <h4 className="font-bold text-xl text-secondary mb-1">{selectedUnit.slug}</h4>
                                    <div className="text-sm text-slate-500 mb-4">{selectedUnit.type}</div>

                                    <div className="space-y-3 text-sm">
                                        <div className="flex justify-between py-2 border-b">
                                            <span className="text-slate-500">{t('project.price', 'Price')}</span>
                                            <span className="font-bold">{selectedUnit.price}</span>
                                        </div>
                                        <div className="flex justify-between py-2 border-b">
                                            <span className="text-slate-500">{t('project.area', 'Area')}</span>
                                            <span className="font-bold">{selectedUnit.area} m²</span>
                                        </div>
                                        <div className="flex justify-between py-2 border-b">
                                            <span className="text-slate-500">{t('project.bedrooms', 'Bedrooms')}</span>
                                            <span className="font-bold">{selectedUnit.beds}</span>
                                        </div>
                                        <div className="flex justify-between py-2 border-b">
                                            <span className="text-slate-500">{t('project.bathrooms', 'Bathrooms')}</span>
                                            <span className="font-bold">{selectedUnit.baths}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-auto">
                                    <a
                                        href={`/units/${selectedUnit.slug}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block w-full py-3 bg-secondary text-white text-center rounded-lg font-medium hover:bg-secondary-highlight transition"
                                    >
                                        {t('common.viewDetails', 'View Full Details')}
                                    </a>
                                </div>
                            </div>
                        )}
                        {!selectedUnit && (
                            <div className="h-full flex flex-col items-center justify-center text-slate-400 p-6 text-center">
                                <Maximize className="w-12 h-12 mb-2 opacity-20" />
                                <p>{t('project.selectUnit', 'Select a unit on the map to see details')}</p>
                            </div>
                        )}
                    </div>

                    {/* Mobile Sheet for Selected Unit (Visible only on mobile when unit selected) */}
                    {selectedUnit && (
                        <div className="md:hidden absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-4 shadow-xl z-[500] animate-slide-up">
                            <div className="flex gap-4">
                                <div className="w-24 h-24 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                                    <RemoteImage src={selectedUnit.image} className="w-full h-full object-cover" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h4 className="font-bold text-lg text-secondary">{selectedUnit.slug}</h4>
                                            <p className="text-xs text-slate-500">{selectedUnit.type} • {selectedUnit.area} m²</p>
                                        </div>
                                        <button onClick={() => setSelectedUnit(null)} className="p-1"><X size={16} /></button>
                                    </div>
                                    <div className="mt-2 text-sm font-bold text-secondary">{selectedUnit.price}</div>
                                    <a
                                        href={`/units/${selectedUnit.slug}`}
                                        className="mt-3 block text-center text-xs bg-secondary text-white py-2 rounded"
                                    >
                                        View Details
                                    </a>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};
